import { CatalogFlow, CatalogNode, nextCatalogId, normalizeCatalogNode } from './catalogDoc';
import { GraphDevice, actuatorChannelOptions } from './graph';
import {
  CatalogWizardArgs,
  ChainState,
  ensureController,
  ensureDashboard,
  ensureDevice,
  ensurePoint,
  ensureTimerMs,
  finishChain,
  link,
  startChain,
  upsertDevice,
  wireToMqtt,
} from './catalogChain';
import { linkStacklightCommand } from './catalogStacklight';

/**
 * stacklight_legacy = same logic layer as modern stacklight (commands, Signal1…N,
 * statuses), but hardware base is gpio_relay / gpio_led (`variant: classic`) —
 * analogous to modern sitting on 4× WS2812. Five channels, fixed Aus/Ein only.
 *
 * Actuators must exist first; classic stacklight references them via `channels[]`.
 */

/** Default classic output channel refs (EVB relay bank + user LED as channel 5). */
export const STACKLIGHT_LEGACY_CHANNEL_REFS = ['relay.1', 'relay.2', 'relay.3', 'relay.4', 'led'];

/** Optional panel inputs on the same board (remote / bidirectional) — Flow wizard only. */
export const STACKLIGHT_LEGACY_INPUT_LINES = [
  { key: 'in1', pin: 1, mode: 'input', edge: 'change', inverted: true, enable: true },
  { key: 'in2', pin: 2, mode: 'input', edge: 'change', inverted: true, enable: true },
  { key: 'in3', pin: 3, mode: 'input', edge: 'change', inverted: true, enable: true },
  { key: 'in4', pin: 15, mode: 'input', edge: 'change', inverted: true, enable: true },
  { key: 'button', pin: 9, mode: 'pullup', edge: 'change', inverted: true, enable: true },
];

export const STACKLIGHT_LEGACY_ZIGBEE_OFFSET: Record<string, number> = {
  button: 0,
  in1: 10,
  in2: 20,
  in3: 30,
  in4: 40,
};

/** Fixed channel colours (Ein). Aus is always off/#000000. */
export const STACKLIGHT_LEGACY_CHANNEL_COLORS = [
  '#ff0000', // Signal1 red
  '#ffff00', // Signal2 yellow
  '#00ff00', // Signal3 green
  '#0000ff', // Signal4 blue
  '#ff00ff', // Signal5 violet (button / LED)
];

/** Default GPIO poll period for panel inputs (Flow wizard). */
export const STACKLIGHT_LEGACY_POLL_MS = 50;

export type LegacyChannel = {
  key: string;
  leaf: string;
  ref: string;
  color: string;
};

export const STACKLIGHT_LEGACY_CHANNELS: LegacyChannel[] = STACKLIGHT_LEGACY_CHANNEL_REFS.map((ref, index) => ({
  key: index < 4 ? `in${index + 1}` : 'button',
  leaf: `Signal${index + 1}`,
  ref,
  color: STACKLIGHT_LEGACY_CHANNEL_COLORS[index] || '#00ff00',
}));

export type StacklightLegacyWizardArgs = CatalogWizardArgs & {
  /** Wire panel gpio_input + zigbee uplink (remote / bidirectional). Default false for periphery. */
  wirePanelInputs?: boolean;
  wireZigbeeUplink?: boolean;
  /** Optional second host: modern stacklight to mirror channels onto. */
  modernHostname?: string;
  modernControllerId?: string;
  modernOf?: string;
  /** Timer poll period in milliseconds when wiring panel inputs. */
  intervalMs?: number;
};

function linesOf(device: GraphDevice) {
  const raw = Array.isArray(device.lines) && device.lines.length
    ? device.lines
    : STACKLIGHT_LEGACY_INPUT_LINES;
  return raw.map((item) => {
    const row = item as Record<string, unknown>;
    return {
      key: String(row.key || '').trim(),
      pin: Number(row.pin),
      mode: String(row.mode || 'input'),
      edge: String(row.edge || 'change'),
      inverted: row.inverted === true,
    };
  }).filter((line) => line.key && Number.isFinite(line.pin));
}

function channelForKey(key: string): LegacyChannel | undefined {
  return STACKLIGHT_LEGACY_CHANNELS.find((row) => row.key === key);
}

/** Classic stacklight device: 5 channel refs → relay/led, Aus/Ein steady only. */
export function buildLegacyStacklightDevice(of = 'stacklight'): GraphDevice {
  return {
    id: of,
    type: 'stacklight',
    enable: true,
    variant: 'classic',
    channels: [...STACKLIGHT_LEGACY_CHANNEL_REFS],
    signals: STACKLIGHT_LEGACY_CHANNELS.map((channel, index) => ({
      id: `s${index + 1}`,
      name: channel.leaf,
      statuses: [
        { id: `s${index + 1}o`, name: 'Aus', color: '#000000', mode: 'off' },
        { id: `s${index + 1}n`, name: 'Ein', color: channel.color, mode: 'steady' },
      ],
    })),
  };
}

/**
 * Settings: classic stacklight that references existing gpio_relay / gpio_led.
 * Actuators are not created here — add them first, then pick channels.
 * Panel inputs are installed so the Flow wizard can wire them later.
 */
export function applyStacklightLegacyHardware(
  values: Record<string, unknown>,
  device: GraphDevice,
): Record<string, unknown> {
  const of = String(device.id || 'stacklight').trim() || 'stacklight';
  const existing = Array.isArray(values.devices)
    ? (values.devices as GraphDevice[])
    : [];
  const options = actuatorChannelOptions(existing).map((row) => row.id);
  const channels = Array.from({ length: 5 }, (_, index) => options[index] || '');
  const classic = {
    ...buildLegacyStacklightDevice(of),
    channels,
  };
  let devices = upsertDevice(values, classic);
  devices = upsertDevice({ devices }, {
    id: 'inputs',
    type: 'gpio_input',
    enable: true,
    lines: linesOf(device),
  });
  return { ...values, devices };
}

export type WireLegacyOpts = {
  flowName: string;
  hostname: string;
  controllerId: string;
  stacklightOf: string;
  lines?: { key: string; pin: number; mode: string; edge: string; inverted: boolean }[];
  keys?: string[];
  interval?: number;
  intervalMs?: number;
  wirePanelInputs?: boolean;
  /** Same-host panel → stacklight.SignalN edges. */
  wireLocalActuators?: boolean;
  wireZigbeeUplink?: boolean;
  modernHostname?: string;
  modernControllerId?: string;
  modernOf?: string;
  widget?: string;
};

/**
 * Catalog graph:
 * 1) Simple (periphery): controller → stacklight → status → mqtt → dashboard
 * 2) Complex (Flow wizard): panel poll → zigbee/mqtt + edges to stacklight.SignalN
 *    and optional mirror host
 */
export function wireStacklightLegacyGraph(state: ChainState, opts: WireLegacyOpts): void {
  const of = String(opts.stacklightOf || 'stacklight').trim() || 'stacklight';
  const legacyArgs: CatalogWizardArgs = {
    flowName: opts.flowName,
    hostname: opts.hostname,
    controllerId: opts.controllerId,
    device: { id: of, type: 'stacklight', enable: true, variant: 'classic' },
    widget: (opts.widget as CatalogWizardArgs['widget']) || 'stacklight',
    interval: opts.interval,
  };
  const controller = ensureController(state, legacyArgs);
  const stacklight = ensureDevice(state, {
    of,
    deviceType: 'stacklight',
    x: state.x0 + 220,
    y: state.y0,
  });
  link(state, `${controller.id}.host`, `${stacklight.id}.host`);

  const status = ensurePoint(state, {
    of,
    key: 'status',
    x: state.x0 + 440,
    y: state.y0,
    deviceId: stacklight.id,
  });
  const stacklightZigbee = opts.wireZigbeeUplink === true && opts.wirePanelInputs !== true;
  const mqttBase = stacklightZigbee
    ? undefined
    : wireToMqtt(state, {
      point: status,
      topic: '{base}',
      y: state.y0,
      xCalc: state.x0 + 640,
      xMqtt: state.x0 + 640,
    });

  if (opts.widget !== 'none') {
    const dash = ensureDashboard(state, {
      widgetType: 'stacklight',
      title: opts.hostname ? `${opts.hostname} · ${of}` : of,
      signalName: of,
      x: state.x0 + 860,
      y: state.y0,
    });
    if (mqttBase) {
      link(state, `${mqttBase.id}.subscribe`, `${dash.id}.in`);
    }
    linkStacklightCommand(state, {
      pointNodeId: status.id,
      controllerNodeId: controller.id,
      dashNodeId: dash.id,
      mqttNodeId: mqttBase?.id,
      zigbee: stacklightZigbee,
      x: state.x0 + 1080,
      y: state.y0,
    });
  }

  if (opts.wirePanelInputs !== true) {
    return;
  }

  const lines = opts.lines && opts.lines.length ? opts.lines : STACKLIGHT_LEGACY_INPUT_LINES.map((line) => ({
    key: line.key,
    pin: line.pin,
    mode: line.mode,
    edge: line.edge,
    inverted: line.inverted,
  }));
  const keys = (opts.keys && opts.keys.length) ? opts.keys : lines.map((line) => line.key);
  const channels = keys
    .map((key) => channelForKey(key))
    .filter((row): row is LegacyChannel => Boolean(row));
  if (!channels.length) {
    return;
  }

  const inputs = ensureDevice(state, {
    of: 'inputs',
    deviceType: 'gpio_input',
    x: state.x0 + 220,
    y: state.y0 + 280,
  });
  link(state, `${controller.id}.host`, `${inputs.id}.read`);

  const intervalMs = Number(opts.intervalMs) > 0
    ? Math.round(Number(opts.intervalMs))
    : (Number(opts.interval) > 0 ? Math.round(Number(opts.interval) * 1000) : STACKLIGHT_LEGACY_POLL_MS);
  const timer = ensureTimerMs(state, intervalMs, state.x0, state.y0 + 480);

  const zigbee = opts.wireZigbeeUplink
    ? ensureDevice(state, {
      of: 'zigbee',
      deviceType: 'zigbee',
      x: state.x0 + 1180,
      y: state.y0 + 280,
    })
    : null;
  if (zigbee) {
    link(state, `${controller.id}.host`, `${zigbee.id}.host`);
  }

  const modernHost = String(opts.modernHostname || '').trim();
  const modernOf = String(opts.modernOf || 'stacklight').trim() || 'stacklight';
  let modernDev: CatalogNode | undefined;
  let modernDash: CatalogNode | undefined;
  if (modernHost) {
    const modernCtrl = ensureController(state, {
      flowName: opts.flowName,
      hostname: modernHost,
      controllerId: opts.modernControllerId || modernHost,
      device: { id: modernOf, type: 'stacklight', enable: true },
      widget: 'stacklight',
    });
    modernCtrl.x = state.x0 + 1420;
    modernCtrl.y = state.y0;
    modernDev = ensureDevice(state, {
      of: modernOf,
      deviceType: 'stacklight',
      x: state.x0 + 1640,
      y: state.y0,
    });
    link(state, `${modernCtrl.id}.host`, `${modernDev.id}.host`);
    if (opts.widget !== 'none') {
      modernDash = ensureDashboard(state, {
        widgetType: 'stacklight',
        title: `${modernHost} · ${modernOf}`,
        signalName: modernOf,
        x: state.x0 + 1860,
        y: state.y0,
      });
    }
  }

  channels.forEach((channel, index) => {
    const y = state.y0 + 280 + index * 100;
    const point = ensurePoint(state, {
      of: 'inputs',
      key: channel.key,
      poll: true,
      x: state.x0 + 460,
      y,
      deviceId: inputs.id,
    });
    link(state, `${timer.id}.trigger`, `${point.id}.trigger`);

    if (opts.wireLocalActuators !== false) {
      // Same-host: panel → this classic stacklight channel (logic layer).
      link(state, `${point.id}.value`, `${stacklight.id}.${channel.leaf}`);
    }

    const mqttIn = wireToMqtt(state, {
      point,
      topic: `{base}/inputs/${channel.key}`,
      y,
      xCalc: state.x0 + 700,
      xMqtt: state.x0 + 940,
      timer,
      from: 'value',
    });

    if (zigbee) {
      const offset = STACKLIGHT_LEGACY_ZIGBEE_OFFSET[channel.key] ?? 0;
      if (offset === 0) {
        link(state, `${point.id}.value`, `${zigbee.id}.in`);
      } else {
        const calc: CatalogNode = {
          id: nextCatalogId('calc', state.nodes),
          type: 'calc',
          x: state.x0 + 700,
          y: y + 36,
          op: '+',
          value: offset,
        };
        state.nodes.push(calc);
        link(state, `${point.id}.value`, `${calc.id}.in`);
        link(state, `${calc.id}.out`, `${zigbee.id}.in`);
      }
    }

    if (modernDev) {
      link(state, `${point.id}.value`, `${modernDev.id}.${channel.leaf}`);
      let mqttOut = state.nodes.find((node) => (
        node.type === 'mqtt_topic' && node.topic === `{base}/${channel.leaf}`
      ));
      if (!mqttOut) {
        mqttOut = {
          id: nextCatalogId('top', state.nodes),
          type: 'mqtt_topic',
          x: state.x0 + 1640,
          y,
          topic: `{base}/${channel.leaf}`,
        };
        state.nodes.push(mqttOut);
      }
      link(state, `${mqttIn.id}.subscribe`, `${mqttOut.id}.publish`);
      if (modernDash) {
        link(state, `${mqttOut.id}.subscribe`, `${modernDash.id}.in`);
      }
    }
  });
}

/** Periphery wizard: simple stacklight + mqtt + dashboard only. */
export function mergeStacklightLegacyCatalog(
  doc: CatalogFlow,
  args: StacklightLegacyWizardArgs,
): CatalogFlow {
  const state = startChain(doc);
  const of = String(args.device.id || 'stacklight').trim() || 'stacklight';
  wireStacklightLegacyGraph(state, {
    flowName: args.flowName,
    hostname: args.hostname,
    controllerId: args.controllerId,
    stacklightOf: of,
    lines: linesOf(args.device),
    interval: args.interval,
    intervalMs: args.intervalMs,
    wirePanelInputs: args.wirePanelInputs === true,
    wireLocalActuators: args.wireLocalActuators !== false,
    wireZigbeeUplink: args.wireZigbeeUplink === true,
    modernHostname: args.modernHostname,
    modernControllerId: args.modernControllerId,
    modernOf: args.modernOf,
    widget: args.widget,
  });
  const next = finishChain(doc, args, state);
  return { ...next, nodes: next.nodes.map(normalizeCatalogNode) };
}
